import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Loader2, Plus, CalendarOff, UserRoundX, Repeat, Check, X, Clock, Inbox,
} from 'lucide-react';
import { staffService } from '../../services/staffService';
import { toast } from 'sonner';
import { fmtDate, fmtTime, todayStr, weekStartOf, addDays } from '../../lib/scheduleFormat';

const KIND_META = {
  time_off: { icon: CalendarOff, label: 'Time off', tint: 'text-violet-600' },
  drop: { icon: UserRoundX, label: 'Cannot work', tint: 'text-rose-600' },
  swap: { icon: Repeat, label: 'Swap', tint: 'text-sky-600' },
};

const STATUS_STYLES = {
  pending: { label: 'With the office', cls: 'border-amber-300 bg-amber-50 text-amber-800' },
  awaiting_colleague: { label: 'Waiting on your colleague', cls: 'border-sky-300 bg-sky-50 text-sky-800' },
  approved: { label: 'Approved', cls: 'border-emerald-300 bg-emerald-50 text-emerald-800' },
  declined: { label: 'Declined', cls: 'border-rose-300 bg-rose-50 text-rose-800' },
  withdrawn: { label: 'Withdrawn', cls: 'border-slate-300 bg-slate-100 text-slate-700' },
  revoked: { label: 'Cancelled by the office', cls: 'border-slate-300 bg-slate-100 text-slate-700' },
};

const shiftLabel = (shift) =>
  shift ? `${fmtDate(shift.date)} · ${fmtTime(shift.start_time)}–${fmtTime(shift.end_time)}` : '—';

const RequestCard = ({ request, onWithdraw, busy }) => {
  const meta = KIND_META[request.kind] || KIND_META.drop;
  const Icon = meta.icon;
  const status = STATUS_STYLES[request.status] || STATUS_STYLES.pending;
  const open = request.status === 'pending' || request.status === 'awaiting_colleague';
  return (
    <Card data-testid={`request-card-${request.id}`}>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-base font-semibold text-gray-900">
              <Icon className={`h-4 w-4 ${meta.tint}`} />
              {request.kind === 'time_off'
                ? (request.start_date === request.end_date
                  ? fmtDate(request.start_date)
                  : `${fmtDate(request.start_date)} – ${fmtDate(request.end_date)}`)
                : shiftLabel(request.shift)}
            </p>
            <p className="mt-0.5 text-sm text-gray-600">
              {meta.label}
              {request.kind === 'swap' && request.target_user_name
                ? ` with ${request.target_user_name}` : ''}
            </p>
            {request.reason && (
              <p className="mt-1 text-xs text-gray-600">&ldquo;{request.reason}&rdquo;</p>
            )}
            {request.decision?.note && (
              <p className="mt-1 text-xs text-gray-700" data-testid={`request-note-${request.id}`}>
                Office: {request.decision.note}
              </p>
            )}
          </div>
          <Badge variant="outline" className={status.cls} data-testid={`request-status-${request.id}`}>
            {status.label}
          </Badge>
        </div>
        {open && (
          <Button
            variant="outline" className="mt-3 h-10 w-full"
            onClick={() => onWithdraw(request)} disabled={busy === request.id}
            data-testid={`request-withdraw-${request.id}`}
          >
            {busy === request.id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <X className="mr-1 h-4 w-4" />}
            Never mind — withdraw
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const NewRequestDialog = ({ open, onOpenChange, shifts, onSaved }) => {
  const [kind, setKind] = useState('time_off');
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [shiftId, setShiftId] = useState('');
  const [targetId, setTargetId] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKind('time_off');
    setStartDate(todayStr());
    setEndDate(todayStr());
    setShiftId(shifts[0]?.id || '');
    setTargetId('');
    setCandidates([]);
    setReason('');
  }, [open, shifts]);

  useEffect(() => {
    if (kind !== 'swap' || !shiftId) { setCandidates([]); return; }
    let cancelled = false;
    staffService.getSwapCandidates(shiftId)
      .then((res) => { if (!cancelled) setCandidates(res.candidates || []); })
      .catch(() => { if (!cancelled) setCandidates([]); });
    return () => { cancelled = true; };
  }, [kind, shiftId]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { kind, reason };
      if (kind === 'time_off') {
        payload.start_date = startDate;
        payload.end_date = endDate;
      } else {
        payload.shift_id = shiftId;
        if (kind === 'swap') payload.target_user_id = targetId;
      }
      await staffService.raiseShiftRequest(payload);
      toast.success(
        kind === 'swap'
          ? 'Sent — your colleague has to agree first, then the office approves it'
          : 'Sent to the office. Nothing changes on the rota until they approve it',
      );
      onOpenChange(false);
      await onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send that request');
    } finally {
      setSaving(false);
    }
  };

  const noShifts = kind !== 'time_off' && shifts.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="new-request-dialog">
        <DialogHeader>
          <DialogTitle>Ask the office</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm">What do you need?</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {Object.entries(KIND_META).map(([key, meta]) => {
                const Icon = meta.icon;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setKind(key)}
                    className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-xs font-medium transition-colors ${
                      kind === key
                        ? 'border-amber-500 bg-amber-50 text-amber-900'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                    data-testid={`request-kind-${key}`}
                  >
                    <Icon className={`h-5 w-5 ${meta.tint}`} />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          {kind === 'time_off' ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500">First day off</Label>
                <Input
                  type="date" className="mt-1" value={startDate} min={todayStr()}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    if (e.target.value > endDate) setEndDate(e.target.value);
                  }}
                  data-testid="request-start-date"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Last day off</Label>
                <Input
                  type="date" className="mt-1" value={endDate} min={startDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="request-end-date"
                />
              </div>
            </div>
          ) : (
            <div>
              <Label className="text-xs text-gray-500">Which shift?</Label>
              {noShifts ? (
                <p className="mt-1 text-sm text-gray-600" data-testid="request-no-shifts">
                  You have no upcoming published shifts to change.
                </p>
              ) : (
                <select
                  className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
                  value={shiftId}
                  onChange={(e) => setShiftId(e.target.value)}
                  data-testid="request-shift"
                >
                  {shifts.map((s) => (
                    <option key={s.id} value={s.id}>
                      {shiftLabel(s)} · {s.position_label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {kind === 'swap' && !noShifts && (
            <div>
              <Label className="text-xs text-gray-500">Swap with</Label>
              <select
                className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                data-testid="request-target"
              >
                <option value="">Choose a colleague…</option>
                {candidates.map((c) => (
                  <option key={c.user_id} value={c.user_id} disabled={!c.available}>
                    {c.name}
                    {c.on_leave ? ' — on leave that day' : c.already_working ? ' — already working that day' : ''}
                  </option>
                ))}
              </select>
              {candidates.length === 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  Nobody else is marked for that position.
                </p>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs text-gray-500">
              Reason {kind === 'swap' ? '(optional)' : ''}
            </Label>
            <Textarea
              className="mt-1" rows={3} value={reason}
              placeholder={kind === 'time_off' ? 'Family wedding…' : 'Hospital appointment…'}
              onChange={(e) => setReason(e.target.value)}
              data-testid="request-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="request-cancel">
            Cancel
          </Button>
          <Button
            className="bg-amber-600 hover:bg-amber-700"
            onClick={submit}
            disabled={saving || noShifts || (kind === 'swap' && !targetId)}
            data-testid="request-submit"
          >
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Send request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Requests = () => {
  const [data, setData] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const today = todayStr();
      const [requests, thisWeek, nextWeek] = await Promise.all([
        staffService.getMyShiftRequests(),
        staffService.getMySchedule(weekStartOf(today)),
        staffService.getMySchedule(addDays(weekStartOf(today), 7)),
      ]);
      setData(requests);
      setShifts(
        [thisWeek, nextWeek]
          .flatMap((week) => (week?.days || []))
          .filter((day) => day.date >= today)
          .flatMap((day) => day.shifts.filter((s) => s.is_mine)),
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load your requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, id, successMessage) => {
    setBusy(id);
    try {
      await fn();
      toast.success(successMessage);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'That did not work');
    } finally {
      setBusy(null);
    }
  };

  const history = useMemo(
    () => (data?.mine || []).filter((r) => r.status !== 'pending' && r.status !== 'awaiting_colleague'),
    [data],
  );
  const openRequests = useMemo(
    () => (data?.mine || []).filter((r) => r.status === 'pending' || r.status === 'awaiting_colleague'),
    [data],
  );

  return (
    <div className="space-y-4" data-testid="staff-requests">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My requests</h1>
          <p className="mt-1 text-sm text-gray-600">
            Ask for time off, say you cannot work a shift, or swap with a colleague. The office has to
            approve every change — nothing moves on the rota until they do.
          </p>
        </div>
        <Button
          className="h-11 bg-amber-600 hover:bg-amber-700"
          onClick={() => setDialogOpen(true)}
          data-testid="staff-new-request"
        >
          <Plus className="mr-1 h-4 w-4" /> New request
        </Button>
      </div>

      {loading ? (
        <div className="flex min-h-[40vh] items-center justify-center" data-testid="staff-requests-loading">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      ) : (
        <div className="space-y-6">
          {(data?.awaiting_my_answer || []).length > 0 && (
            <section data-testid="swaps-awaiting-me">
              <h2 className="mb-2 text-base font-semibold text-gray-900 md:text-lg">
                Waiting on you
              </h2>
              <div className="space-y-2">
                {data.awaiting_my_answer.map((request) => (
                  <Card key={request.id} className="border-l-4 border-l-sky-500"
                    data-testid={`swap-ask-${request.id}`}>
                    <CardContent className="p-4">
                      <p className="text-base font-semibold text-gray-900">
                        {request.user_name} wants you to take {shiftLabel(request.shift)}
                      </p>
                      <p className="mt-0.5 text-sm text-gray-600">
                        {request.shift?.position_code
                          ? request.shift.position_code.charAt(0).toUpperCase() + request.shift.position_code.slice(1)
                          : 'Shift'}
                        {request.reason ? ` · “${request.reason}”` : ''}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        Saying yes sends it to the office for approval — it is not final until they approve.
                      </p>
                      <div className="mt-3 flex gap-2">
                        <Button
                          className="h-11 flex-1 bg-emerald-600 hover:bg-emerald-700"
                          disabled={busy === request.id}
                          onClick={() => act(
                            () => staffService.respondToSwap(request.id, true), request.id,
                            'Thanks — sent to the office for approval',
                          )}
                          data-testid={`swap-accept-${request.id}`}
                        >
                          <Check className="mr-1 h-4 w-4" /> I can take it
                        </Button>
                        <Button
                          variant="outline" className="h-11 flex-1"
                          disabled={busy === request.id}
                          onClick={() => act(
                            () => staffService.respondToSwap(request.id, false), request.id,
                            'Declined — they have been told',
                          )}
                          data-testid={`swap-decline-${request.id}`}
                        >
                          <X className="mr-1 h-4 w-4" /> I cannot
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}

          <section>
            <h2 className="mb-2 flex items-center gap-2 text-base font-semibold text-gray-900 md:text-lg">
              <Clock className="h-4 w-4 text-amber-600" /> Open requests
            </h2>
            {openRequests.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center" data-testid="no-open-requests">
                  <Inbox className="mx-auto h-8 w-8 text-gray-300" />
                  <p className="mt-2 text-sm text-gray-600">Nothing outstanding.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {openRequests.map((request) => (
                  <RequestCard
                    key={request.id} request={request} busy={busy}
                    onWithdraw={(r) => act(
                      () => staffService.withdrawShiftRequest(r.id), r.id, 'Withdrawn',
                    )}
                  />
                ))}
              </div>
            )}
          </section>

          {history.length > 0 && (
            <section>
              <h2 className="mb-2 text-base font-semibold text-gray-900 md:text-lg">Earlier</h2>
              <div className="space-y-2">
                {history.map((request) => (
                  <RequestCard key={request.id} request={request} busy={busy} onWithdraw={() => {}} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <NewRequestDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        shifts={shifts}
        onSaved={load}
      />
    </div>
  );
};

export default Requests;
